import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
  Checkbox,
  Backdrop,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import EmailIcon from "@mui/icons-material/Email";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkIcon from "@mui/icons-material/Link";
import TagIcon from "@mui/icons-material/Tag";

import SuiButton from "components/SuiButton";
import FileDialog from "components/FileDialog";

import ComunicacionesApi from "api/comunicacionesApi";
import ProcesosApi from "api/procesosApi";

// Formatear fecha a DD-MM-YYYY
const formatearFecha = (fecha) => {
  if (!fecha) return "-";
  const partes = fecha.split("-");
  if (partes.length === 3) {
    return `${partes[2]}-${partes[1]}-${partes[0]}`;
  }
  return fecha;
};

// Formatear hora a formato 12h con am/pm
const formatearHora = (hora) => {
  if (!hora) return "-";
  const partes = hora.split(":");
  if (partes.length >= 2) {
    let horas = parseInt(partes[0], 10);
    const minutos = partes[1];
    const periodo = horas >= 12 ? "pm" : "am";
    horas = horas % 12 || 12;
    return `${horas.toString().padStart(2, "0")}:${minutos} ${periodo}`;
  }
  return hora;
};

// Validar formato de email (soporta múltiples emails separados por comas)
const isValidEmail = (email) => {
  if (!email) return true; // Email vacío es válido
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Si contiene comas, validar cada email por separado
  if (email.includes(",")) {
    const emails = email.split(",").map((e) => e.trim());
    return emails.every((e) => e === "" || emailRegex.test(e));
  }

  // Validar un solo email
  return emailRegex.test(email.trim());
};

// Componente EmailField independiente (fuera del componente principal para evitar re-creación)
const EmailField = ({ value, onChange, placeholder, isEditing, onToggleEdit, onCancelEdit }) => {
  const isInvalid = value && !isValidEmail(value);
  const hasMultipleEmails = value && value.includes(",");

  // Generar helper text dinámico
  const getHelperText = () => {
    if (isInvalid) return "Email(s) inválido(s)";
    if (hasMultipleEmails) {
      const emailCount = value.split(",").filter((e) => e.trim()).length;
      return `${emailCount} emails detectados (separados por comas)`;
    }
    return "Puede ingresar varios emails separados por comas";
  };

  if (!isEditing) {
    return (
      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
        <EditIcon
          fontSize="small"
          sx={{
            cursor: "pointer",
            color: "primary.main",
            "&:hover": { color: "primary.dark" },
          }}
          onClick={onToggleEdit}
        />
        <Typography variant="caption" sx={{ fontSize: "14px" }}>
          {value || "No especificado"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5, width: "100%" }}>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus
          style={{
            width: "100%",
            minWidth: "200px",
            padding: "8.5px 14px",
            fontSize: "14px",
            fontFamily: "inherit",
            border: isInvalid ? "1px solid #d32f2f" : "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.2s",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = isInvalid ? "#d32f2f" : "#1976d2";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isInvalid ? "#d32f2f" : "rgba(0, 0, 0, 0.23)";
          }}
        />
        {getHelperText() && (
          <Typography
            variant="caption"
            sx={{
              color: isInvalid ? "error.main" : "text.secondary",
              fontSize: "12px",
              ml: 1.75,
              mt: 0.375,
            }}
          >
            {getHelperText()}
          </Typography>
        )}
      </Box>
      <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
        <SaveIcon
          fontSize="small"
          sx={{
            cursor: "pointer",
            color: "success.main",
            "&:hover": { color: "success.dark" },
          }}
          onClick={onToggleEdit}
        />
        <CancelIcon
          fontSize="small"
          sx={{
            cursor: "pointer",
            color: "error.main",
            "&:hover": { color: "error.dark" },
          }}
          onClick={onCancelEdit}
        />
      </Box>
    </Box>
  );
};

// PropTypes para el componente EmailField
EmailField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isEditing: PropTypes.bool.isRequired,
  onToggleEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
};

const NotificarComunicacion = ({ open, onClose, comunicacion, onSuccess }) => {
  const [error, setError] = useState(undefined);
  const [selectedDestinatarios, setSelectedDestinatarios] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [saving, setSaving] = useState(false);

  // Estados para edición de emails
  const [editedDestinatarios, setEditedDestinatarios] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [updatingEmails, setUpdatingEmails] = useState(false);

  // Estado para controlar qué email está en modo edición
  const [editingEmails, setEditingEmails] = useState({});

  // Estados para polling de actualización de estados
  const [isPolling, setIsPolling] = useState(false);
  const [pollingIntervalId, setPollingIntervalId] = useState(null);
  const [comunicacionActualizada, setComunicacionActualizada] = useState(null);

  // Estados para visualización de PDF
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfTitle, setPdfTitle] = useState("");
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Estado para la información de la audiencia
  const [audienciaData, setAudienciaData] = useState(null);

  // Función para eliminar emails duplicados en acreedores
  // IMPORTANTE: NO eliminar correos solo por tener el mismo email
  // Cada correo en el array tiene su propio estado de notificación y orden
  const removeDuplicateEmails = (destinatarios) => {
    if (!destinatarios || !destinatarios.acreedores) return destinatarios;

    const cleanedDestinatarios = { ...destinatarios };
    cleanedDestinatarios.acreedores = destinatarios.acreedores.map((acreedor) => {
      if (!acreedor.correos || !Array.isArray(acreedor.correos)) return acreedor;

      // Eliminar duplicados basados en email + orden (no solo email)
      // Dos correos con el mismo email pero diferente orden son DIFERENTES envíos
      const uniqueCorreos = [];
      const seenKeys = new Set();

      acreedor.correos.forEach((correo) => {
        const emailLower = correo.email?.toLowerCase().trim();
        const orden = correo.orden ?? uniqueCorreos.length; // Usar índice si no hay orden
        const uniqueKey = `${emailLower}_${orden}`;

        if (emailLower && !seenKeys.has(uniqueKey)) {
          seenKeys.add(uniqueKey);
          uniqueCorreos.push(correo);
        }
      });

      return { ...acreedor, correos: uniqueCorreos };
    });

    return cleanedDestinatarios;
  };

  // Inicializar destinatarios editables cuando el modal se abre
  useEffect(() => {
    if (comunicacion && open) {
      // Clonar destinatarios profundamente para edición
      let destinatariosClonados = JSON.parse(JSON.stringify(comunicacion.destinatarios));

      // Eliminar emails duplicados en acreedores
      destinatariosClonados = removeDuplicateEmails(destinatariosClonados);

      setEditedDestinatarios(destinatariosClonados);
      setHasChanges(false);
      setComunicacionActualizada(null);
    }
  }, [comunicacion, open]);

  // Obtener información de la audiencia cuando el modal se abre
  useEffect(() => {
    const fetchAudiencia = async () => {
      if (comunicacion && open && comunicacion.audiencia_id) {
        try {
          const response = await ProcesosApi.fetchAudienciaById(comunicacion.audiencia_id);
          if (response.data) {
            setAudienciaData(response.data);
          }
        } catch (error) {
          console.error("Error obteniendo información de la audiencia:", error);
          setAudienciaData(null);
        }
      } else {
        setAudienciaData(null);
      }
    };

    fetchAudiencia();
  }, [comunicacion, open]);

  // Limpiar polling cuando el componente se desmonte o el modal se cierre
  useEffect(() => {
    return () => {
      if (pollingIntervalId) {
        clearInterval(pollingIntervalId);
      }
    };
  }, [pollingIntervalId]);

  // Función para verificar si todos los correos han terminado de enviarse
  const todosCorreosCompletados = (destinatarios) => {
    if (!destinatarios) return true;

    // Verificar deudor con correos[]
    if (destinatarios.deudor?.correos) {
      const hayPendientes = destinatarios.deudor.correos.some(
        (correo) => correo.notificado === "pending_confirmation"
      );
      if (hayPendientes) return false;
    }

    // Verificar acreedores con correos[]
    if (destinatarios.acreedores) {
      for (const acreedor of destinatarios.acreedores) {
        if (acreedor.correos) {
          const hayPendientes = acreedor.correos.some(
            (correo) => correo.notificado === "pending_confirmation"
          );
          if (hayPendientes) return false;
        }
      }
    }

    // Verificar otros destinatarios simples
    const destinatariosSimples = [
      destinatarios.deudor_apoderado,
      ...(destinatarios.centrales_riesgo || []),
      ...(destinatarios.juzgados || []),
      ...(destinatarios.pagadores_libranza || []),
    ].filter(Boolean);

    for (const dest of destinatariosSimples) {
      if (dest.notificado === "pending_confirmation") {
        return false;
      }
    }

    return true;
  };

  // Función para hacer polling de la comunicación
  const iniciarPolling = (comunicacionId, procesoId) => {
    console.log(
      "[POLLING] Iniciando polling para comunicación",
      comunicacionId,
      "proceso",
      procesoId
    );
    setIsPolling(true);

    const intervalId = setInterval(async () => {
      try {
        console.log("[POLLING] Consultando estado de comunicación...");
        const response = await ComunicacionesApi.obtenerComunicacionById(comunicacionId, procesoId);

        if (response.status === 200 && response.data) {
          const comunicacionFresca = response.data;
          console.log("[POLLING] Comunicación actualizada:", comunicacionFresca);

          // Actualizar la comunicación en el estado local
          setComunicacionActualizada(comunicacionFresca);

          // Actualizar destinatarios editables también
          let destinatariosClonados = JSON.parse(JSON.stringify(comunicacionFresca.destinatarios));
          destinatariosClonados = removeDuplicateEmails(destinatariosClonados);
          setEditedDestinatarios(destinatariosClonados);

          // Verificar si todos los correos han terminado de enviarse
          if (todosCorreosCompletados(comunicacionFresca.destinatarios)) {
            console.log("[POLLING] Todos los correos completados, deteniendo polling");
            clearInterval(intervalId);
            setIsPolling(false);
            setPollingIntervalId(null);
            setSaving(false);

            // Mostrar mensaje de éxito
            alert("Todos los emails han sido procesados correctamente.");
            onSuccess();
          }
        }
      } catch (error) {
        console.error("[POLLING] Error consultando comunicación:", error);
        // No detener el polling por un error temporal
      }
    }, 3000); // Polling cada 3 segundos

    setPollingIntervalId(intervalId);

    // Timeout de seguridad: detener polling después de 5 minutos
    setTimeout(() => {
      if (intervalId) {
        console.log("[POLLING] Timeout alcanzado, deteniendo polling");
        clearInterval(intervalId);
        setIsPolling(false);
        setPollingIntervalId(null);
        setSaving(false);
      }
    }, 300000); // 5 minutos
  };

  if (!comunicacion) return null;

  // Función para generar y visualizar PDF personalizado por destinatario
  const handleViewPdfDestinatario = async (destinatario, destinatarioTipo) => {
    if (!comunicacion) return;

    // Limpiar URL anterior si existe
    if (pdfUrl && pdfUrl.startsWith("blob:")) {
      URL.revokeObjectURL(pdfUrl);
    }

    try {
      setLoadingPdf(true);
      const timestamp = Date.now();
      const destinatarioNombre = destinatario.nombre_completo || destinatario.nombre || "";
      const destinatarioId = destinatario.id || null;

      // Mapear el tipo de destinatario al valor técnico para el backend
      let tipoDestinatarioBackend = null;
      if (comunicacion.formato_comunicacion === "notificacionAdmision") {
        const tipoMapping = {
          "Central de Riesgo": "central_riesgo",
          Juzgado: "juzgado",
          "Pagador de Libranza": "pagador_libranza",
        };
        tipoDestinatarioBackend = tipoMapping[destinatarioTipo] || null;
      }

      const pdfResponse = await ComunicacionesApi.generateComunicacionPdf(
        comunicacion.id,
        { responseType: "blob" },
        timestamp,
        destinatarioNombre,
        destinatarioId,
        tipoDestinatarioBackend
      );

      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const uniqueUrl = `${url}#${timestamp}`;

      setPdfTitle(`Notificación para ${destinatarioNombre} - ${destinatarioTipo}`);
      setPdfUrl(uniqueUrl);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error generando PDF de notificación:", error);
      alert(obtenerMensajeError(error, "Error al generar el PDF de notificación"));
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDestinatarioChange = (key, checked) => {
    setSelectedDestinatarios((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const newSelected = {};
    if (comunicacion) {
      const destinatarios = comunicacion.destinatarios || {};
      // Para notificacionManual con estructura de lista
      if (
        comunicacion.formato_comunicacion === "notificacionManual" &&
        Array.isArray(destinatarios)
      ) {
        destinatarios.forEach((_, index) => {
          newSelected[`destinatario_${index}`] = checked;
        });
      }
      // Para comunicación física (estructura original)
      else if (comunicacion.tipo_comunicacion === "FISICA" && destinatarios.acreedor) {
        newSelected["acreedor"] = checked;
      }
      // Para comunicación EMAIL (estructura original)
      else {
        if (destinatarios.deudor) {
          newSelected["deudor"] = checked;
        }
        if (destinatarios.deudor_apoderado) {
          newSelected["deudor_apoderado"] = checked;
        }
        if (destinatarios.centrales_riesgo && destinatarios.centrales_riesgo.length > 0) {
          destinatarios.centrales_riesgo.forEach((_, index) => {
            newSelected[`central_${index}`] = checked;
          });
        }
        if (destinatarios.juzgados && destinatarios.juzgados.length > 0) {
          destinatarios.juzgados.forEach((_, index) => {
            newSelected[`juzgado_${index}`] = checked;
          });
        }
        if (destinatarios.pagadores_libranza && destinatarios.pagadores_libranza.length > 0) {
          destinatarios.pagadores_libranza.forEach((_, index) => {
            newSelected[`pagador_libranza_${index}`] = checked;
          });
        }
        if (destinatarios.acreedores && destinatarios.acreedores.length > 0) {
          destinatarios.acreedores.forEach((acreedor, index) => {
            // Seleccionar el acreedor principal solo si tiene email
            const acreedorTieneEmail = acreedor.correos && acreedor.correos.length > 0;
            if (acreedorTieneEmail) {
              newSelected[`acreedor_${index}`] = checked;
            }
            // Seleccionar apoderado del acreedor si existe y tiene email
            if (acreedor.apoderado && acreedor.apoderado.email) {
              newSelected[`acreedor_${index}_apoderado`] = checked;
            }
          });
        }
      }
    }
    setSelectedDestinatarios(newSelected);
  };

  // Handlers para editar emails
  // Para notificacionManual (array)
  const handleEmailChangeManual = (index, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newDest = [...prev];
      newDest[index] = { ...newDest[index], email: newEmail };
      return newDest;
    });
    setHasChanges(true);
  };

  // Para destinatarios EMAIL simples (deudor, deudor_apoderado)
  const handleEmailChangeSimple = (key, newEmail) => {
    setEditedDestinatarios((prev) => ({
      ...prev,
      [key]: { ...prev[key], email: newEmail },
    }));
    setHasChanges(true);
  };

  // Para deudor con múltiples correos
  const handleEmailChangeDeudorCorreo = (correoIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newCorreos = [...prev.deudor.correos];
      newCorreos[correoIndex] = { ...newCorreos[correoIndex], email: newEmail };
      return {
        ...prev,
        deudor: { ...prev.deudor, correos: newCorreos },
      };
    });
    setHasChanges(true);
  };

  // Para arrays de destinatarios (centrales_riesgo, juzgados, pagadores_libranza)
  const handleEmailChangeArray = (arrayKey, index, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newArray = [...prev[arrayKey]];
      newArray[index] = { ...newArray[index], email: newEmail };
      return { ...prev, [arrayKey]: newArray };
    });
    setHasChanges(true);
  };

  // Para acreedores con múltiples correos
  const handleEmailChangeAcreedorCorreo = (acreedorIndex, correoIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newAcreedores = [...prev.acreedores];
      const newCorreos = [...newAcreedores[acreedorIndex].correos];
      newCorreos[correoIndex] = { ...newCorreos[correoIndex], email: newEmail };
      newAcreedores[acreedorIndex] = { ...newAcreedores[acreedorIndex], correos: newCorreos };
      return { ...prev, acreedores: newAcreedores };
    });
    setHasChanges(true);
  };

  // Para juzgados con múltiples correos
  const handleEmailChangeJuzgadoCorreo = (juzgadoIndex, correoIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newJuzgados = [...prev.juzgados];
      const newCorreos = [...newJuzgados[juzgadoIndex].correos];
      newCorreos[correoIndex] = { ...newCorreos[correoIndex], email: newEmail };
      newJuzgados[juzgadoIndex] = { ...newJuzgados[juzgadoIndex], correos: newCorreos };
      return { ...prev, juzgados: newJuzgados };
    });
    setHasChanges(true);
  };

  // Para centrales de riesgo con múltiples correos
  const handleEmailChangeCentralCorreo = (centralIndex, correoIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newCentrales = [...prev.centrales_riesgo];
      const newCorreos = [...newCentrales[centralIndex].correos];
      newCorreos[correoIndex] = { ...newCorreos[correoIndex], email: newEmail };
      newCentrales[centralIndex] = { ...newCentrales[centralIndex], correos: newCorreos };
      return { ...prev, centrales_riesgo: newCentrales };
    });
    setHasChanges(true);
  };

  // Para pagadores libranza con múltiples correos
  const handleEmailChangePagadorCorreo = (pagadorIndex, correoIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newPagadores = [...prev.pagadores_libranza];
      const newCorreos = [...newPagadores[pagadorIndex].correos];
      newCorreos[correoIndex] = { ...newCorreos[correoIndex], email: newEmail };
      newPagadores[pagadorIndex] = { ...newPagadores[pagadorIndex], correos: newCorreos };
      return { ...prev, pagadores_libranza: newPagadores };
    });
    setHasChanges(true);
  };

  // Para apoderado del deudor con múltiples correos
  const handleEmailChangeApoderadoDeudorCorreo = (correoIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newCorreos = [...prev.deudor_apoderado.correos];
      newCorreos[correoIndex] = { ...newCorreos[correoIndex], email: newEmail };
      return {
        ...prev,
        deudor_apoderado: { ...prev.deudor_apoderado, correos: newCorreos },
      };
    });
    setHasChanges(true);
  };

  // Para apoderado de acreedor
  const handleEmailChangeAcreedorApoderado = (acreedorIndex, newEmail) => {
    setEditedDestinatarios((prev) => {
      const newAcreedores = [...prev.acreedores];
      newAcreedores[acreedorIndex] = {
        ...newAcreedores[acreedorIndex],
        apoderado: { ...newAcreedores[acreedorIndex].apoderado, email: newEmail },
      };
      return { ...prev, acreedores: newAcreedores };
    });
    setHasChanges(true);
  };

  // Función para obtener el estado de un correo individual
  const getCorreoStatus = (correo) => {
    if (!correo) {
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: "Sin información de correo",
        label: "Sin info",
      };
    }

    // CASO 1: notificado es TRUE → Email confirmado por webhook
    if (correo.notificado === true && !correo.error_envio) {
      return {
        icon: <CheckCircleIcon />,
        color: "success",
        tooltip: "Email entregado exitosamente (confirmado por webhook)",
        label: "Entregado",
      };
    }

    // CASO 2: notificado es "pending_confirmation" → Email aceptado por SendGrid, esperando webhook
    if (correo.notificado === "pending_confirmation") {
      return {
        icon: <HourglassEmptyIcon />,
        color: "info",
        tooltip: "Email enviado a SendGrid, esperando confirmación de entrega...",
        label: "Enviando...",
      };
    }

    // CASO 3: notificado es FALSE y hay error_envio → Falló el envío
    if (correo.notificado === false && correo.error_envio) {
      // Determinar el mensaje según el tipo de error
      let mensajeError = "Error de envío";
      if (correo.error_envio === "rebote") {
        mensajeError = "REBOTE: El email no existe o fue rechazado por el servidor";
      } else if (correo.error_envio === "invalido") {
        mensajeError = "Email inválido o con formato incorrecto";
      } else if (correo.error_envio === "bloqueado") {
        mensajeError = "Email bloqueado o en lista negra";
      } else if (correo.error_envio === "temporal") {
        mensajeError = "Error temporal del servidor - reintentar más tarde";
      } else if (correo.error_envio === "spam") {
        mensajeError = "Email marcado como spam o rechazado por filtros";
      } else {
        mensajeError = `Error: ${correo.error_envio}`;
      }

      return {
        icon: <ErrorIcon />,
        color: "error",
        tooltip: mensajeError,
        label: "Error",
      };
    }

    // CASO 4: notificado es FALSE y NO hay error → Pendiente de envío (aún no se ha intentado)
    if (correo.notificado === false && !correo.error_envio) {
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: "Pendiente de envío - no se ha enviado aún",
        label: "Pendiente",
      };
    }

    // Caso por defecto
    return {
      icon: <HourglassEmptyIcon />,
      color: "warning",
      tooltip: "Estado desconocido",
      label: "Desconocido",
    };
  };

  // Función para obtener el estado consolidado de un destinatario basado en todos sus correos
  const getDestinatarioConsolidadoStatus = (destinatario) => {
    if (!destinatario) {
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: "Sin información",
        label: "Sin info",
      };
    }

    // Para destinatarios con un solo email directo (deudor sin correos[], centrales de riesgo, etc.)
    if (destinatario.email && !destinatario.correos) {
      return getCorreoStatus(destinatario);
    }

    // Para destinatarios con múltiples correos (deudor con correos[], acreedores con correos[])
    if (destinatario.correos && Array.isArray(destinatario.correos)) {
      const correos = destinatario.correos;
      const totalCorreos = correos.length;

      // Contar estados
      const correosEntregados = correos.filter(
        (correo) => correo.notificado === true && !correo.error_envio
      ).length;
      const correosEnviando = correos.filter(
        (correo) => correo.notificado === "pending_confirmation"
      ).length;
      const correosConError = correos.filter(
        (correo) => correo.notificado === false && correo.error_envio
      ).length;
      const correosPendientes = correos.filter(
        (correo) => correo.notificado === false && !correo.error_envio
      ).length;

      // CASO 1: Todos los correos están entregados (confirmed por webhook)
      if (correosEntregados === totalCorreos) {
        return {
          icon: <CheckCircleIcon />,
          color: "success",
          tooltip: `Todos los correos entregados exitosamente (${totalCorreos}/${totalCorreos})`,
          label: "Entregados",
        };
      }

      // CASO 2: Todos los correos fallaron
      if (correosConError === totalCorreos) {
        return {
          icon: <ErrorIcon />,
          color: "error",
          tooltip: `Todos los correos fallaron (${totalCorreos}/${totalCorreos})`,
          label: "Error total",
        };
      }

      // CASO 3: Algunos correos con errores (envío parcial)
      if (correosConError > 0) {
        return {
          icon: <ErrorIcon />,
          color: "error",
          tooltip: `${correosConError} de ${totalCorreos} correos con errores - Envío parcial`,
          label: "Parcial con errores",
        };
      }

      // CASO 4: Hay correos enviando (pending_confirmation)
      if (correosEnviando > 0) {
        return {
          icon: <HourglassEmptyIcon />,
          color: "info",
          tooltip: `${correosEnviando} de ${totalCorreos} correos enviándose... (${correosEntregados} ya entregados)`,
          label: "Enviando...",
        };
      }

      // CASO 5: Algunos están entregados pero otros pendientes (aún no enviados)
      if (correosEntregados > 0 && correosPendientes > 0) {
        return {
          icon: <HourglassEmptyIcon />,
          color: "warning",
          tooltip: `${correosEntregados} de ${totalCorreos} correos entregados - ${correosPendientes} pendientes`,
          label: "Parcial",
        };
      }

      // CASO 6: Todos están pendientes (no se ha enviado ninguno)
      if (correosPendientes === totalCorreos) {
        return {
          icon: <HourglassEmptyIcon />,
          color: "warning",
          tooltip: `${totalCorreos} correos pendientes de envío`,
          label: "Pendiente",
        };
      }

      // Fallback
      return {
        icon: <HourglassEmptyIcon />,
        color: "warning",
        tooltip: `Estado mixto: ${correosEntregados} entregados, ${correosEnviando} enviando, ${correosConError} con error, ${correosPendientes} pendientes`,
        label: "Mixto",
      };
    }

    // Fallback para el campo notificado directo (compatibilidad con estructura antigua)
    if (destinatario.notificado === true) {
      return {
        icon: <CheckCircleIcon />,
        color: "success",
        tooltip: "Notificado correctamente",
        label: "Notificado",
      };
    }

    if (destinatario.notificado === "pending_confirmation") {
      return {
        icon: <HourglassEmptyIcon />,
        color: "info",
        tooltip: "Enviando...",
        label: "Enviando...",
      };
    }

    return {
      icon: <HourglassEmptyIcon />,
      color: "warning",
      tooltip: "Pendiente de notificación",
      label: "Pendiente",
    };
  };

  // Funciones para controlar edición de emails
  const toggleEditEmail = (key) => {
    setEditingEmails((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const cancelEditEmail = (key) => {
    setEditingEmails((prev) => ({
      ...prev,
      [key]: false,
    }));
    // Restaurar valor original si se cancela
    if (comunicacion && editedDestinatarios) {
      let destinatariosClonados = JSON.parse(JSON.stringify(comunicacion.destinatarios));
      // Eliminar emails duplicados en acreedores al restaurar
      destinatariosClonados = removeDuplicateEmails(destinatariosClonados);
      setEditedDestinatarios(destinatariosClonados);
      setHasChanges(false);
    }
  };

  // Función para guardar emails editados
  const handleSaveEmails = async () => {
    if (!editedDestinatarios) return;

    // Validar todos los emails
    const invalidEmails = [];

    if (Array.isArray(editedDestinatarios)) {
      // notificacionManual
      editedDestinatarios.forEach((dest, idx) => {
        if (dest.email && !isValidEmail(dest.email)) {
          invalidEmails.push(`Destinatario ${idx + 1}: ${dest.email}`);
        }
      });
    } else {
      // EMAIL con estructura compleja
      if (editedDestinatarios.deudor?.email && !isValidEmail(editedDestinatarios.deudor.email)) {
        invalidEmails.push(`Deudor: ${editedDestinatarios.deudor.email}`);
      }
      if (
        editedDestinatarios.deudor_apoderado?.email &&
        !isValidEmail(editedDestinatarios.deudor_apoderado.email)
      ) {
        invalidEmails.push(`Apoderado Deudor: ${editedDestinatarios.deudor_apoderado.email}`);
      }
      // Validar acreedores con correos múltiples
      if (editedDestinatarios.acreedores) {
        editedDestinatarios.acreedores.forEach((acreedor, idx) => {
          if (acreedor.correos && Array.isArray(acreedor.correos)) {
            acreedor.correos.forEach((correo, correoIdx) => {
              if (correo.email && !isValidEmail(correo.email)) {
                invalidEmails.push(
                  `Acreedor ${idx + 1} - Correo ${correoIdx + 1}: ${correo.email}`
                );
              }
            });
          }
          if (acreedor.apoderado?.email && !isValidEmail(acreedor.apoderado.email)) {
            invalidEmails.push(`Apoderado Acreedor ${idx + 1}: ${acreedor.apoderado.email}`);
          }
        });
      }
      // Validar juzgados
      if (editedDestinatarios.juzgados) {
        editedDestinatarios.juzgados.forEach((juzgado, idx) => {
          if (juzgado.email && !isValidEmail(juzgado.email)) {
            invalidEmails.push(`Juzgado ${idx + 1}: ${juzgado.email}`);
          }
        });
      }
      // Validar pagadores libranza
      if (editedDestinatarios.pagadores_libranza) {
        editedDestinatarios.pagadores_libranza.forEach((pagador, idx) => {
          if (pagador.email && !isValidEmail(pagador.email)) {
            invalidEmails.push(`Pagador Libranza ${idx + 1}: ${pagador.email}`);
          }
        });
      }
      // Validar centrales de riesgo
      if (editedDestinatarios.centrales_riesgo) {
        editedDestinatarios.centrales_riesgo.forEach((central, idx) => {
          if (central.email && !isValidEmail(central.email)) {
            invalidEmails.push(`Central de Riesgo ${idx + 1}: ${central.email}`);
          }
        });
      }
    }

    if (invalidEmails.length > 0) {
      alert(`Los siguientes emails son inválidos:\n${invalidEmails.join("\n")}`);
      return;
    }

    try {
      setUpdatingEmails(true);

      // Preparar datos para actualización
      const dataToUpdate = {
        proceso: comunicacion.proceso_id,
        audiencia: comunicacion.audiencia_id,
        tipo_comunicacion: comunicacion.tipo_comunicacion,
        formato_comunicacion: comunicacion.formato_comunicacion,
        asunto: comunicacion.asunto,
        contenido: comunicacion.contenido || "",
        destinatarios: editedDestinatarios,
        creado_por: comunicacion.creado_por,
      };

      await ComunicacionesApi.actualizarComunicacion(comunicacion.id, dataToUpdate);

      alert("Emails actualizados correctamente");
      setHasChanges(false);

      // Actualizar comunicación local
      comunicacion.destinatarios = editedDestinatarios;
    } catch (error) {
      console.error("Error actualizando emails:", error);
      alert(obtenerMensajeError(error, "Error al actualizar los emails"));
    } finally {
      setUpdatingEmails(false);
    }
  };

  const renderDestinatarios = () => {
    if (!comunicacion) return null;

    // Usar comunicación actualizada si está disponible (durante polling)
    // pero mantener editedDestinatarios para la edición
    const comunicacionBase = comunicacionActualizada || comunicacion;
    const destinatarios = editedDestinatarios || comunicacionBase.destinatarios || {};
    // Para notificacionManual con estructura de lista
    if (
      comunicacion.formato_comunicacion === "notificacionManual" &&
      Array.isArray(destinatarios)
    ) {
      return (
        <>
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold", mb: 2 }}>
            Destinatarios de la Comunicación
          </Typography>
          <List disablePadding>
            {destinatarios.map((destinatario, index) => (
              <ListItem key={`destinatario-${index}`}>
                <ListItemIcon>
                  <Checkbox
                    checked={selectedDestinatarios[`destinatario_${index}`] || false}
                    onChange={(e) =>
                      handleDestinatarioChange(`destinatario_${index}`, e.target.checked)
                    }
                    color="primary"
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    "& .MuiListItemText-primary": { marginBottom: -1 },
                    "& .MuiListItemText-secondary": { marginTop: -1 },
                  }}
                  secondaryTypographyProps={{ component: "div" }}
                  primary={
                    <Box display="flex" alignItems="center">
                      <Tooltip title="Ver PDF de Notificación">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            handleViewPdfDestinatario(destinatario, "Notificación Manual")
                          }
                          disabled={loadingPdf}
                          sx={{ mr: 1 }}
                        >
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="body1" component="span" fontWeight="medium">
                        {destinatario.nombre_completo || "Sin nombre"}
                      </Typography>
                      {(() => {
                        const status = getDestinatarioConsolidadoStatus(destinatario);
                        return (
                          <Chip
                            icon={status.icon}
                            label={status.label}
                            color={status.color}
                            size="small"
                            sx={{ ml: 2 }}
                            title={status.tooltip}
                          />
                        );
                      })()}
                    </Box>
                  }
                  secondary={
                    comunicacion.tipo_comunicacion === "EMAIL" ? (
                      <EmailField
                        value={editedDestinatarios?.[index]?.email || ""}
                        onChange={(newEmail) => handleEmailChangeManual(index, newEmail)}
                        placeholder="Ingrese email"
                        isEditing={editingEmails[`destinatario_${index}`] || false}
                        onToggleEdit={() => toggleEditEmail(`destinatario_${index}`)}
                        onCancelEdit={() => cancelEditEmail(`destinatario_${index}`)}
                      />
                    ) : (
                      <Box component="span">
                        <Typography variant="caption" sx={{ fontSize: "14px" }}>
                          {destinatario.email || "No especificado"}
                        </Typography>
                      </Box>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      );
    }
    // Si es una comunicación física
    if (comunicacion.tipo_comunicacion === "FISICA" && destinatarios.acreedor) {
      const acreedor = destinatarios.acreedor;
      return (
        <List disablePadding>
          <ListItem>
            <ListItemIcon>
              <Checkbox
                checked={selectedDestinatarios["acreedor"] || false}
                onChange={(e) => handleDestinatarioChange("acreedor", e.target.checked)}
                color="primary"
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center">
                  <Typography variant="body1" component="span" fontWeight="medium">
                    {acreedor.nombre || "Sin nombre"}
                  </Typography>
                  <Chip
                    icon={<LocalShippingIcon fontSize="small" />}
                    label="Físico"
                    size="small"
                    color="secondary"
                  />
                </Box>
              }
              secondary={
                <Box component="span">
                  <Typography variant="body2" component="span">
                    Dirección: {acreedor.direccion || "No especificada"}
                  </Typography>
                  <br />
                  <Typography variant="body2" component="span">
                    Ciudad: {acreedor.ciudad || "No especificada"}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        </List>
      );
    } else {
      // Para comunicaciones por EMAIL
      return (
        <>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
            {/* Deudor */}
            {destinatarios.deudor && (
              <>
                <Grid size={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                    Deudor
                  </Typography>
                  <List disablePadding>
                    <ListItem>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedDestinatarios["deudor"] || false}
                          onChange={(e) => handleDestinatarioChange("deudor", e.target.checked)}
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          "& .MuiListItemText-primary": { marginBottom: -1 },
                          "& .MuiListItemText-secondary": { marginTop: -1 },
                        }}
                        primary={
                          <Box display="flex" alignItems="center">
                            <Tooltip title="Ver PDF de Notificación">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleViewPdfDestinatario(destinatarios.deudor, "Deudor")
                                }
                                disabled={loadingPdf}
                                sx={{ mr: 1 }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography variant="body1" component="span" fontWeight="medium">
                              {destinatarios.deudor.nombre_completo || "Sin nombre"}
                            </Typography>
                            {(() => {
                              const status = getDestinatarioConsolidadoStatus(destinatarios.deudor);
                              return (
                                <Chip
                                  icon={status.icon}
                                  label={status.label}
                                  color={status.color}
                                  size="small"
                                  sx={{ ml: 2 }}
                                  title={status.tooltip}
                                />
                              );
                            })()}
                          </Box>
                        }
                        secondaryTypographyProps={{ component: "div" }}
                        secondary={
                          <Box>
                            {destinatarios.deudor.correos &&
                            Array.isArray(destinatarios.deudor.correos) &&
                            destinatarios.deudor.correos.length > 0 ? (
                              // Deudor con múltiples correos
                              <Box>
                                {destinatarios.deudor.correos.map((correo, correoIndex) => {
                                  const correoStatus = getCorreoStatus(correo);
                                  return (
                                    <Box
                                      key={`deudor-correo-${correoIndex}`}
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      sx={{ mb: 0.5 }}
                                    >
                                      <Box sx={{ minWidth: 0 }}>
                                        <EmailField
                                          value={
                                            editedDestinatarios?.deudor?.correos?.[correoIndex]
                                              ?.email || ""
                                          }
                                          onChange={(newEmail) =>
                                            handleEmailChangeDeudorCorreo(correoIndex, newEmail)
                                          }
                                          placeholder="Ingrese email del deudor"
                                          isEditing={
                                            editingEmails[`deudor_correo_${correoIndex}`] || false
                                          }
                                          onToggleEdit={() =>
                                            toggleEditEmail(`deudor_correo_${correoIndex}`)
                                          }
                                          onCancelEdit={() =>
                                            cancelEditEmail(`deudor_correo_${correoIndex}`)
                                          }
                                        />
                                      </Box>
                                      <Chip
                                        icon={correoStatus.icon}
                                        label={correoStatus.label}
                                        color={correoStatus.color}
                                        size="small"
                                        sx={{ height: 24, fontSize: "11px", ml: 1 }}
                                        title={correoStatus.tooltip}
                                      />
                                    </Box>
                                  );
                                })}
                              </Box>
                            ) : (
                              // Deudor con email simple (compatibilidad con estructura antigua)
                              <EmailField
                                value={editedDestinatarios?.deudor?.email || ""}
                                onChange={(newEmail) => handleEmailChangeSimple("deudor", newEmail)}
                                placeholder="Ingrese email del deudor"
                                isEditing={editingEmails["deudor"] || false}
                                onToggleEdit={() => toggleEditEmail("deudor")}
                                onCancelEdit={() => cancelEditEmail("deudor")}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </>
            )}
            {/* Apoderado Deudor */}
            {destinatarios.deudor_apoderado && (
              <>
                <Grid size={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                    Apoderado del Deudor
                  </Typography>
                  <List disablePadding>
                    <ListItem>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedDestinatarios["deudor_apoderado"] || false}
                          onChange={(e) =>
                            handleDestinatarioChange("deudor_apoderado", e.target.checked)
                          }
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          "& .MuiListItemText-primary": { marginBottom: -1 },
                          "& .MuiListItemText-secondary": { marginTop: -1 },
                        }}
                        secondaryTypographyProps={{ component: "div" }}
                        primary={
                          <Box display="flex" alignItems="center">
                            <Tooltip title="Ver PDF de Notificación">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleViewPdfDestinatario(
                                    destinatarios.deudor_apoderado,
                                    "Apoderado del Deudor"
                                  )
                                }
                                disabled={loadingPdf}
                                sx={{ mr: 1 }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography variant="body1" component="span" fontWeight="medium">
                              {destinatarios.deudor_apoderado.nombre_completo || "Sin nombre"}
                            </Typography>
                            {(() => {
                              const status = getDestinatarioConsolidadoStatus(
                                destinatarios.deudor_apoderado
                              );
                              return (
                                <Chip
                                  icon={status.icon}
                                  label={status.label}
                                  color={status.color}
                                  size="small"
                                  sx={{ ml: 2 }}
                                  title={status.tooltip}
                                />
                              );
                            })()}
                          </Box>
                        }
                        secondary={
                          <Box>
                            {destinatarios.deudor_apoderado.correos &&
                            Array.isArray(destinatarios.deudor_apoderado.correos) &&
                            destinatarios.deudor_apoderado.correos.length > 0 ? (
                              // Apoderado con múltiples correos (estructura correos[])
                              <Box>
                                {destinatarios.deudor_apoderado.correos.map(
                                  (correo, correoIndex) => {
                                    const correoStatus = getCorreoStatus(correo);
                                    return (
                                      <Box
                                        key={`apoderado-deudor-correo-${correoIndex}`}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        sx={{ mb: 0.5 }}
                                      >
                                        <Box sx={{ minWidth: 0 }}>
                                          <EmailField
                                            value={
                                              editedDestinatarios?.deudor_apoderado?.correos?.[
                                                correoIndex
                                              ]?.email || ""
                                            }
                                            onChange={(newEmail) =>
                                              handleEmailChangeApoderadoDeudorCorreo(
                                                correoIndex,
                                                newEmail
                                              )
                                            }
                                            placeholder="Ingrese email del apoderado del deudor"
                                            isEditing={
                                              editingEmails[
                                                `deudor_apoderado_correo_${correoIndex}`
                                              ] || false
                                            }
                                            onToggleEdit={() =>
                                              toggleEditEmail(
                                                `deudor_apoderado_correo_${correoIndex}`
                                              )
                                            }
                                            onCancelEdit={() =>
                                              cancelEditEmail(
                                                `deudor_apoderado_correo_${correoIndex}`
                                              )
                                            }
                                          />
                                        </Box>
                                        <Chip
                                          icon={correoStatus.icon}
                                          label={correoStatus.label}
                                          color={correoStatus.color}
                                          size="small"
                                          sx={{ height: 24, fontSize: "11px", ml: 1 }}
                                          title={correoStatus.tooltip}
                                        />
                                      </Box>
                                    );
                                  }
                                )}
                              </Box>
                            ) : (
                              // Apoderado con email simple (compatibilidad con estructura antigua)
                              <EmailField
                                value={editedDestinatarios?.deudor_apoderado?.email || ""}
                                onChange={(newEmail) =>
                                  handleEmailChangeSimple("deudor_apoderado", newEmail)
                                }
                                placeholder="Ingrese email del apoderado del deudor"
                                isEditing={editingEmails["deudor_apoderado"] || false}
                                onToggleEdit={() => toggleEditEmail("deudor_apoderado")}
                                onCancelEdit={() => cancelEditEmail("deudor_apoderado")}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
              </>
            )}
          </Grid>

          {/* Acreedores */}
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            {destinatarios.acreedores && destinatarios.acreedores.length > 0 && (
              <>
                <Grid size={12}>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                    Acreedores y Apoderados
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <List disablePadding>
                    {destinatarios.acreedores.map((acreedor, index) => {
                      const acreedorTieneEmail = acreedor.correos && acreedor.correos.length > 0;
                      const apoderadoTieneEmail = acreedor.apoderado?.email;

                      return (
                        <Box key={`acreedor-${index}-${acreedor.id || index}`} sx={{ mb: 1 }}>
                          <Grid container spacing={1}>
                            {/* Acreedor Principal */}
                            <Grid size={acreedor.apoderado ? 6 : 12}>
                              <ListItem sx={{ opacity: acreedorTieneEmail ? 1 : 0.5 }}>
                                <ListItemIcon>
                                  <Checkbox
                                    checked={selectedDestinatarios[`acreedor_${index}`] || false}
                                    onChange={(e) =>
                                      handleDestinatarioChange(
                                        `acreedor_${index}`,
                                        e.target.checked
                                      )
                                    }
                                    color="primary"
                                    disabled={!acreedorTieneEmail}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  secondaryTypographyProps={{ component: "div" }}
                                  primary={
                                    <Box display="flex" alignItems="center">
                                      <Tooltip title="Ver PDF de Notificación">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() =>
                                            handleViewPdfDestinatario(acreedor, "Acreedor")
                                          }
                                          disabled={loadingPdf}
                                          sx={{ mr: 1 }}
                                        >
                                          <PictureAsPdfIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Typography variant="body1" fontWeight="medium">
                                        {acreedor.nombre_completo || "Acreedor sin nombre"}
                                      </Typography>
                                      {(() => {
                                        const status = getDestinatarioConsolidadoStatus(acreedor);
                                        return (
                                          <Chip
                                            icon={status.icon}
                                            label={status.label}
                                            color={status.color}
                                            size="small"
                                            sx={{ ml: 2 }}
                                            title={status.tooltip}
                                          />
                                        );
                                      })()}
                                    </Box>
                                  }
                                  secondary={
                                    <Box>
                                      {acreedor.correos && acreedor.correos.length > 0 ? (
                                        <Box>
                                          {acreedor.correos.map((correo, correoIndex) => {
                                            const correoStatus = getCorreoStatus(correo);
                                            return (
                                              <Box
                                                key={`correo-${correoIndex}`}
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                sx={{ mb: 0.5 }}
                                              >
                                                <Box sx={{ minWidth: 0 }}>
                                                  <EmailField
                                                    value={
                                                      editedDestinatarios?.acreedores?.[index]
                                                        ?.correos?.[correoIndex]?.email || ""
                                                    }
                                                    onChange={(newEmail) =>
                                                      handleEmailChangeAcreedorCorreo(
                                                        index,
                                                        correoIndex,
                                                        newEmail
                                                      )
                                                    }
                                                    placeholder="Ingrese email del acreedor"
                                                    isEditing={
                                                      editingEmails[
                                                        `acreedor_${index}_correo_${correoIndex}`
                                                      ] || false
                                                    }
                                                    onToggleEdit={() =>
                                                      toggleEditEmail(
                                                        `acreedor_${index}_correo_${correoIndex}`
                                                      )
                                                    }
                                                    onCancelEdit={() =>
                                                      cancelEditEmail(
                                                        `acreedor_${index}_correo_${correoIndex}`
                                                      )
                                                    }
                                                  />
                                                </Box>
                                                <Chip
                                                  icon={correoStatus.icon}
                                                  label={correoStatus.label}
                                                  color={correoStatus.color}
                                                  size="small"
                                                  sx={{ height: 24, fontSize: "11px", ml: 1 }}
                                                  title={correoStatus.tooltip}
                                                />
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      ) : (
                                        <Typography variant="caption" sx={{ fontSize: "12px" }}>
                                          Sin correos registrados
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            </Grid>

                            {/* Apoderado del acreedor */}
                            {acreedor.apoderado && (
                              <Grid size={6}>
                                <ListItem sx={{ opacity: apoderadoTieneEmail ? 1 : 0.5 }}>
                                  <ListItemIcon>
                                    <Checkbox
                                      checked={
                                        selectedDestinatarios[`acreedor_${index}_apoderado`] ||
                                        false
                                      }
                                      onChange={(e) =>
                                        handleDestinatarioChange(
                                          `acreedor_${index}_apoderado`,
                                          e.target.checked
                                        )
                                      }
                                      color="primary"
                                      disabled={!apoderadoTieneEmail}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    secondaryTypographyProps={{ component: "div" }}
                                    primary={
                                      <Box display="flex" alignItems="center">
                                        <Tooltip title="Ver PDF de Notificación">
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                              handleViewPdfDestinatario(
                                                acreedor.apoderado,
                                                "Apoderado de Acreedor"
                                              )
                                            }
                                            disabled={loadingPdf}
                                            sx={{ mr: 1 }}
                                          >
                                            <PictureAsPdfIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Typography variant="body1" fontWeight="medium">
                                          {acreedor.apoderado.nombre_completo ||
                                            "Apoderado sin nombre"}
                                        </Typography>
                                        {(() => {
                                          const status = getDestinatarioConsolidadoStatus(
                                            acreedor.apoderado
                                          );
                                          return (
                                            <Chip
                                              icon={status.icon}
                                              label={status.label}
                                              color={status.color}
                                              size="small"
                                              sx={{ ml: 2 }}
                                              title={status.tooltip}
                                            />
                                          );
                                        })()}
                                      </Box>
                                    }
                                    secondary={
                                      <EmailField
                                        value={
                                          editedDestinatarios?.acreedores?.[index]?.apoderado
                                            ?.email || ""
                                        }
                                        onChange={(newEmail) =>
                                          handleEmailChangeAcreedorApoderado(index, newEmail)
                                        }
                                        placeholder="Ingrese email del apoderado"
                                        isEditing={
                                          editingEmails[`acreedor_${index}_apoderado`] || false
                                        }
                                        onToggleEdit={() =>
                                          toggleEditEmail(`acreedor_${index}_apoderado`)
                                        }
                                        onCancelEdit={() =>
                                          cancelEditEmail(`acreedor_${index}_apoderado`)
                                        }
                                      />
                                    }
                                  />
                                </ListItem>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      );
                    })}
                  </List>
                </Grid>
              </>
            )}
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            {/* Juzgados */}
            {destinatarios.juzgados && destinatarios.juzgados.length > 0 && (
              <>
                <Grid size={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                    Juzgados
                  </Typography>
                  <List disablePadding>
                    {destinatarios.juzgados.map((juzgado, index) => (
                      <ListItem key={`juzgado-${index}`}>
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedDestinatarios[`juzgado_${index}`] || false}
                            onChange={(e) =>
                              handleDestinatarioChange(`juzgado_${index}`, e.target.checked)
                            }
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            "& .MuiListItemText-primary": { marginBottom: -1 },
                            "& .MuiListItemText-secondary": { marginTop: -1 },
                          }}
                          secondaryTypographyProps={{ component: "div" }}
                          primary={
                            <Box display="flex" alignItems="center">
                              <Tooltip title="Ver PDF de Notificación">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewPdfDestinatario(juzgado, "Juzgado")}
                                  disabled={loadingPdf}
                                  sx={{ mr: 1 }}
                                >
                                  <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="body1" component="span" fontWeight="medium">
                                {juzgado.nombre || "Juzgado sin nombre"}
                              </Typography>
                              {(() => {
                                const status = getDestinatarioConsolidadoStatus(juzgado);
                                return (
                                  <Chip
                                    icon={status.icon}
                                    label={status.label}
                                    color={status.color}
                                    size="small"
                                    sx={{ ml: 2 }}
                                    title={status.tooltip}
                                  />
                                );
                              })()}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {juzgado.correos &&
                              Array.isArray(juzgado.correos) &&
                              juzgado.correos.length > 0 ? (
                                // Juzgado con múltiples correos (estructura correos[])
                                <Box>
                                  {juzgado.correos.map((correo, correoIndex) => {
                                    const correoStatus = getCorreoStatus(correo);
                                    return (
                                      <Box
                                        key={`juzgado-${index}-correo-${correoIndex}`}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        sx={{ mb: 0.5 }}
                                      >
                                        <Box sx={{ minWidth: 0 }}>
                                          <EmailField
                                            value={
                                              editedDestinatarios?.juzgados?.[index]?.correos?.[
                                                correoIndex
                                              ]?.email || ""
                                            }
                                            onChange={(newEmail) =>
                                              handleEmailChangeJuzgadoCorreo(
                                                index,
                                                correoIndex,
                                                newEmail
                                              )
                                            }
                                            placeholder="Ingrese email del juzgado"
                                            isEditing={
                                              editingEmails[
                                                `juzgado_${index}_correo_${correoIndex}`
                                              ] || false
                                            }
                                            onToggleEdit={() =>
                                              toggleEditEmail(
                                                `juzgado_${index}_correo_${correoIndex}`
                                              )
                                            }
                                            onCancelEdit={() =>
                                              cancelEditEmail(
                                                `juzgado_${index}_correo_${correoIndex}`
                                              )
                                            }
                                          />
                                        </Box>
                                        <Chip
                                          icon={correoStatus.icon}
                                          label={correoStatus.label}
                                          color={correoStatus.color}
                                          size="small"
                                          sx={{ height: 24, fontSize: "11px", ml: 1 }}
                                          title={correoStatus.tooltip}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                // Juzgado con email simple (compatibilidad con estructura antigua)
                                <EmailField
                                  value={editedDestinatarios?.juzgados?.[index]?.email || ""}
                                  onChange={(newEmail) =>
                                    handleEmailChangeArray("juzgados", index, newEmail)
                                  }
                                  placeholder="Ingrese email del juzgado"
                                  isEditing={editingEmails[`juzgado_${index}`] || false}
                                  onToggleEdit={() => toggleEditEmail(`juzgado_${index}`)}
                                  onCancelEdit={() => cancelEditEmail(`juzgado_${index}`)}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </>
            )}

            {/* Pagadores_Libranza */}
            {destinatarios.pagadores_libranza && destinatarios.pagadores_libranza.length > 0 && (
              <>
                <Grid size={6}>
                  <Typography variant="subtitle1" color="primary" sx={{ fontWeight: "bold" }}>
                    Pagadores Libranza
                  </Typography>
                  <List disablePadding>
                    {destinatarios.pagadores_libranza.map((pagador_libranza, index) => (
                      <ListItem key={`pagador_libranza-${index}`}>
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedDestinatarios[`pagador_libranza_${index}`] || false}
                            onChange={(e) =>
                              handleDestinatarioChange(
                                `pagador_libranza_${index}`,
                                e.target.checked
                              )
                            }
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            "& .MuiListItemText-primary": { marginBottom: -1 },
                            "& .MuiListItemText-secondary": { marginTop: -1 },
                          }}
                          secondaryTypographyProps={{ component: "div" }}
                          primary={
                            <Box display="flex" alignItems="center">
                              <Tooltip title="Ver PDF de Notificación">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleViewPdfDestinatario(
                                      pagador_libranza,
                                      "Pagador de Libranza"
                                    )
                                  }
                                  disabled={loadingPdf}
                                  sx={{ mr: 1 }}
                                >
                                  <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="body1" component="span" fontWeight="medium">
                                {pagador_libranza.nombre_completo || "Pagador Libranza sin nombre"}
                              </Typography>
                              {(() => {
                                const status = getDestinatarioConsolidadoStatus(pagador_libranza);
                                return (
                                  <Chip
                                    icon={status.icon}
                                    label={status.label}
                                    color={status.color}
                                    size="small"
                                    sx={{ ml: 2 }}
                                    title={status.tooltip}
                                  />
                                );
                              })()}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {pagador_libranza.correos &&
                              Array.isArray(pagador_libranza.correos) &&
                              pagador_libranza.correos.length > 0 ? (
                                // Pagador con múltiples correos (estructura correos[])
                                <Box>
                                  {pagador_libranza.correos.map((correo, correoIndex) => {
                                    const correoStatus = getCorreoStatus(correo);
                                    return (
                                      <Box
                                        key={`pagador-${index}-correo-${correoIndex}`}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        sx={{ mb: 0.5 }}
                                      >
                                        <Box sx={{ minWidth: 0 }}>
                                          <EmailField
                                            value={
                                              editedDestinatarios?.pagadores_libranza?.[index]
                                                ?.correos?.[correoIndex]?.email || ""
                                            }
                                            onChange={(newEmail) =>
                                              handleEmailChangePagadorCorreo(
                                                index,
                                                correoIndex,
                                                newEmail
                                              )
                                            }
                                            placeholder="Ingrese email del pagador libranza"
                                            isEditing={
                                              editingEmails[
                                                `pagador_libranza_${index}_correo_${correoIndex}`
                                              ] || false
                                            }
                                            onToggleEdit={() =>
                                              toggleEditEmail(
                                                `pagador_libranza_${index}_correo_${correoIndex}`
                                              )
                                            }
                                            onCancelEdit={() =>
                                              cancelEditEmail(
                                                `pagador_libranza_${index}_correo_${correoIndex}`
                                              )
                                            }
                                          />
                                        </Box>
                                        <Chip
                                          icon={correoStatus.icon}
                                          label={correoStatus.label}
                                          color={correoStatus.color}
                                          size="small"
                                          sx={{ height: 24, fontSize: "11px", ml: 1 }}
                                          title={correoStatus.tooltip}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                // Pagador con email simple (compatibilidad con estructura antigua)
                                <EmailField
                                  value={
                                    editedDestinatarios?.pagadores_libranza?.[index]?.email || ""
                                  }
                                  onChange={(newEmail) =>
                                    handleEmailChangeArray("pagadores_libranza", index, newEmail)
                                  }
                                  placeholder="Ingrese email del pagador libranza"
                                  isEditing={editingEmails[`pagador_libranza_${index}`] || false}
                                  onToggleEdit={() => toggleEditEmail(`pagador_libranza_${index}`)}
                                  onCancelEdit={() => cancelEditEmail(`pagador_libranza_${index}`)}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </>
            )}
          </Grid>

          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            {/* Centrales de Riesgo */}
            {destinatarios.centrales_riesgo && destinatarios.centrales_riesgo.length > 0 && (
              <>
                <Grid size={12}>
                  <Typography
                    variant="subtitle1"
                    color="primary"
                    sx={{ mt: 2, fontWeight: "bold" }}
                  >
                    Centrales de Riesgo
                  </Typography>
                  <List disablePadding>
                    {destinatarios.centrales_riesgo.map((central, index) => (
                      <ListItem key={`central-${index}`}>
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedDestinatarios[`central_${index}`] || false}
                            onChange={(e) =>
                              handleDestinatarioChange(`central_${index}`, e.target.checked)
                            }
                            color="primary"
                          />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            "& .MuiListItemText-primary": { marginBottom: -1 },
                            "& .MuiListItemText-secondary": { marginTop: -1 },
                          }}
                          secondaryTypographyProps={{ component: "div" }}
                          primary={
                            <Box display="flex" alignItems="center">
                              <Tooltip title="Ver PDF de Notificación">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleViewPdfDestinatario(central, "Central de Riesgo")
                                  }
                                  disabled={loadingPdf}
                                  sx={{ mr: 1 }}
                                >
                                  <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Typography variant="body1" component="span" fontWeight="medium">
                                {central.nombre || "Central sin nombre"}
                              </Typography>
                              {(() => {
                                const status = getDestinatarioConsolidadoStatus(central);
                                return (
                                  <Chip
                                    icon={status.icon}
                                    label={status.label}
                                    color={status.color}
                                    size="small"
                                    sx={{ ml: 2 }}
                                    title={status.tooltip}
                                  />
                                );
                              })()}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {central.correos &&
                              Array.isArray(central.correos) &&
                              central.correos.length > 0 ? (
                                // Central con múltiples correos (estructura correos[])
                                <Box>
                                  {central.correos.map((correo, correoIndex) => {
                                    const correoStatus = getCorreoStatus(correo);
                                    return (
                                      <Box
                                        key={`central-${index}-correo-${correoIndex}`}
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        sx={{ mb: 0.5 }}
                                      >
                                        <Box sx={{ minWidth: 0 }}>
                                          <EmailField
                                            value={
                                              editedDestinatarios?.centrales_riesgo?.[index]
                                                ?.correos?.[correoIndex]?.email || ""
                                            }
                                            onChange={(newEmail) =>
                                              handleEmailChangeCentralCorreo(
                                                index,
                                                correoIndex,
                                                newEmail
                                              )
                                            }
                                            placeholder="Ingrese email de central de riesgo"
                                            isEditing={
                                              editingEmails[
                                                `central_${index}_correo_${correoIndex}`
                                              ] || false
                                            }
                                            onToggleEdit={() =>
                                              toggleEditEmail(
                                                `central_${index}_correo_${correoIndex}`
                                              )
                                            }
                                            onCancelEdit={() =>
                                              cancelEditEmail(
                                                `central_${index}_correo_${correoIndex}`
                                              )
                                            }
                                          />
                                        </Box>
                                        <Chip
                                          icon={correoStatus.icon}
                                          label={correoStatus.label}
                                          color={correoStatus.color}
                                          size="small"
                                          sx={{ height: 24, fontSize: "11px", ml: 1 }}
                                          title={correoStatus.tooltip}
                                        />
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                // Central con email simple (compatibilidad con estructura antigua)
                                <EmailField
                                  value={
                                    editedDestinatarios?.centrales_riesgo?.[index]?.email || ""
                                  }
                                  onChange={(newEmail) =>
                                    handleEmailChangeArray("centrales_riesgo", index, newEmail)
                                  }
                                  placeholder="Ingrese email de central de riesgo"
                                  isEditing={editingEmails[`central_${index}`] || false}
                                  onToggleEdit={() => toggleEditEmail(`central_${index}`)}
                                  onCancelEdit={() => cancelEditEmail(`central_${index}`)}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </>
            )}
          </Grid>
        </>
      );
    }
  };

  const getSelectedCount = () => {
    if (!comunicacion) return 0;
    const destinatarios = comunicacion.destinatarios || {};
    const uniqueSelected = new Set();
    // Para notificacionManual con estructura de lista
    if (
      comunicacion.formato_comunicacion === "notificacionManual" &&
      Array.isArray(destinatarios)
    ) {
      destinatarios.forEach((destinatario, index) => {
        if (selectedDestinatarios[`destinatario_${index}`]) {
          uniqueSelected.add(
            `destinatario_${destinatario.email || destinatario.nombre_completo || index}`
          );
        }
      });
      return uniqueSelected.size;
    }
    // Para comunicación física
    if (comunicacion.tipo_comunicacion === "FISICA" && destinatarios.acreedor) {
      if (selectedDestinatarios["acreedor"]) {
        uniqueSelected.add(
          `acreedor_${destinatarios.acreedor.email || destinatarios.acreedor.nombre}`
        );
      }
      return uniqueSelected.size;
    }
    // Para comunicación por EMAIL
    // Deudor
    if (destinatarios.deudor && selectedDestinatarios["deudor"]) {
      uniqueSelected.add(`deudor_${destinatarios.deudor.email}`);
    }
    // Apoderado del deudor
    if (destinatarios.deudor_apoderado && selectedDestinatarios["deudor_apoderado"]) {
      uniqueSelected.add(`deudor_apoderado_${destinatarios.deudor_apoderado.email}`);
    }
    // Centrales de riesgo
    if (destinatarios.centrales_riesgo) {
      destinatarios.centrales_riesgo.forEach((central, index) => {
        if (selectedDestinatarios[`central_${index}`]) {
          uniqueSelected.add(`central_${central.email}`);
        }
      });
    }
    // Juzgado
    if (destinatarios.juzgados) {
      destinatarios.juzgados.forEach((juzgado, index) => {
        if (selectedDestinatarios[`juzgado_${index}`]) {
          uniqueSelected.add(`juzgado_${juzgado.email}`);
        }
      });
    }
    // Juzgado
    if (destinatarios.pagadores_libranza) {
      destinatarios.pagadores_libranza.forEach((pagador_libranza, index) => {
        if (selectedDestinatarios[`pagador_libranza_${index}`]) {
          uniqueSelected.add(`pagador_libranza_${pagador_libranza.email}`);
        }
      });
    }
    // Acreedores
    if (destinatarios.acreedores) {
      destinatarios.acreedores.forEach((acreedor, index) => {
        // Contar acreedor principal
        if (selectedDestinatarios[`acreedor_${index}`]) {
          uniqueSelected.add(`acreedor_${index}_${acreedor.email || acreedor.id}`);
        }

        // Contar apoderado del acreedor
        if (acreedor.apoderado && selectedDestinatarios[`acreedor_${index}_apoderado`]) {
          uniqueSelected.add(
            `acreedor_${index}_apoderado_${acreedor.apoderado.email || acreedor.apoderado.id}`
          );
        }
      });
    }
    return uniqueSelected.size;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Verificar que al menos un destinatario esté seleccionado
    const hasSelected = Object.values(selectedDestinatarios).some((selected) => selected);
    if (!hasSelected) {
      alert("Debe seleccionar al menos un destinatario para notificar.");
      return;
    }
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea notificar la comunicación a los destinatarios seleccionados?`
    );
    if (!confirmacion) return;

    try {
      setSaving(true);
      setError(undefined);

      const datos = {
        operacion: "NOTIFICAR_COMUNICACION",
        id_comunicacion: comunicacion.id,
        destinatarios_seleccionados: selectedDestinatarios,
      };

      // Configurar timeout más largo para el request inicial (2 minutos)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minutos

      try {
        const response = await ComunicacionesApi.operarComunicacion(datos);

        clearTimeout(timeoutId);

        if (response.status === 200) {
          console.log("[ENVÍO] Comunicación enviada, iniciando polling...");

          // Iniciar polling para actualizar estados en tiempo real
          iniciarPolling(comunicacion.id, comunicacion.proceso_id);

          // El mensaje de éxito se mostrará cuando el polling termine
        }
      } catch (requestError) {
        clearTimeout(timeoutId);

        // Si el error es por timeout/abort, iniciar polling de todas formas
        if (requestError.name === "AbortError" || requestError.code === "ECONNABORTED") {
          console.log("[ENVÍO] Request timeout - el backend está procesando. Iniciando polling...");

          // Mostrar mensaje al usuario
          alert(
            obtenerMensajeError(
              requestError,
              "El proceso de envío está en curso. Los estados se actualizarán automáticamente. Por favor, mantenga esta ventana abierta."
            )
          );

          // Iniciar polling de todas formas
          iniciarPolling(comunicacion.id, comunicacion.proceso_id);
        } else {
          // Error real, no timeout
          throw requestError;
        }
      }
    } catch (error) {
      console.error("Error notificando la comunicación:", error);
      setError(
        obtenerMensajeError(
          error,
          "Error al notificar la comunicación. Por favor, verifique el estado manualmente."
        )
      );
      setSaving(false);
      setIsPolling(false);
    }
    // NO llamar setSaving(false) aquí - el polling lo hará cuando termine
  };

  const resetState = () => {
    setError(undefined);
    setSelectedDestinatarios({});
    setSelectAll(false);
    setEditedDestinatarios(null);
    setHasChanges(false);
    setEditingEmails({}); // Limpiar estado de edición de emails
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <>
      {saving && !isPolling && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" color="inherit">
              Enviando comunicación...
            </Typography>
          </Box>
        </Backdrop>
      )}
      <Dialog open={open} onClose={hCClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" fontWeight="bold">
              Notificar Comunicación
            </Typography>
            {isPolling && (
              <Box display="flex" alignItems="center" gap={1} sx={{ color: "info.main" }}>
                <CircularProgress size={20} color="inherit" />
                <Typography variant="body2" color="inherit">
                  Actualizando estados...
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {/* Info del Proceso */}
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Proceso: {comunicacion?.proceso_identificador || "No tiene Proceso"}
            </Typography>
            {/* Info de Tipo y Formato de Comunicación */}
            {comunicacion?.tipo_comunicacion && (
              <Box display="flex" alignItems="center" mt={1} gap={1}>
                {comunicacion.tipo_comunicacion === "EMAIL" ? (
                  <Chip
                    icon={<EmailIcon />}
                    label="Por Email"
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<LocalShippingIcon />}
                    label="Física"
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                )}
                <Chip label={comunicacion.formato_comunicacion} size="small" variant="outlined" />
              </Box>
            )}
          </Box>

          {/* Info de Audiencia con Chips */}
          {audienciaData && (
            <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
              {audienciaData.consecutivo && (
                <Chip
                  icon={<EventIcon />}
                  label={`#${audienciaData.consecutivo}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              <Chip
                icon={<EventIcon />}
                label={formatearFecha(audienciaData.fecha)}
                size="small"
                variant="outlined"
              />
              {audienciaData.hora && (
                <Chip
                  icon={<AccessTimeIcon />}
                  label={formatearHora(audienciaData.hora)}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                icon={
                  audienciaData.modalidad?.toUpperCase() === "VIRTUAL" ? (
                    <VideocamIcon />
                  ) : (
                    <LocationOnIcon />
                  )
                }
                label={audienciaData.modalidad || "-"}
                size="small"
                color={audienciaData.modalidad?.toUpperCase() === "VIRTUAL" ? "info" : "default"}
                variant="outlined"
              />
              {/* Info de Zoom o Lugar según modalidad */}
              {audienciaData.modalidad?.toUpperCase() === "VIRTUAL" ? (
                <>
                  {audienciaData.enlace_zoom && (
                    <Chip
                      icon={<LinkIcon />}
                      label={audienciaData.enlace_zoom}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ maxWidth: 200 }}
                      title={audienciaData.enlace_zoom}
                      onClick={() => window.open(audienciaData.enlace_zoom, "_blank")}
                    />
                  )}
                  {audienciaData.id_zoom && (
                    <Chip
                      icon={<TagIcon />}
                      label={`ID: ${audienciaData.id_zoom}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </>
              ) : (
                audienciaData.lugar && (
                  <Chip
                    icon={<LocationOnIcon />}
                    label={audienciaData.lugar}
                    size="small"
                    variant="outlined"
                    sx={{ maxWidth: 200 }}
                    title={audienciaData.lugar}
                  />
                )
              )}
            </Box>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>

          {comunicacion && (
            <>
              {/* Checkbox para seleccionar/deseleccionar todos */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    color="primary"
                  />
                  <Typography variant="body1" fontWeight="medium">
                    Seleccionar todos los destinatarios
                  </Typography>
                </Box>
                <Typography variant="body2" color="primary" fontWeight="medium">
                  {getSelectedCount()} destinatario(s) seleccionado(s)
                </Typography>
              </Box>
              {renderDestinatarios()}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} color="secondary">
            Cancelar
          </SuiButton>
          {comunicacion?.tipo_comunicacion === "EMAIL" && hasChanges && (
            <SuiButton
              variant="gradient"
              buttonColor="info"
              onClick={handleSaveEmails}
              disabled={updatingEmails}
            >
              {updatingEmails ? "Guardando..." : "Guardar Emails"}
            </SuiButton>
          )}
          <SuiButton
            variant="gradient"
            buttonColor="dark"
            onClick={handleSubmit}
            disabled={getSelectedCount() === 0 || hasChanges}
          >
            {hasChanges ? "Guarde cambios primero" : `Notificar (${getSelectedCount()})`}
          </SuiButton>
        </DialogActions>
      </Dialog>

      {/* Backdrop para carga de PDF */}
      {loadingPdf && (
        <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" color="inherit">
              Generando PDF...
            </Typography>
          </Box>
        </Backdrop>
      )}

      {/* Visor de PDF usando FileDialog */}
      <FileDialog
        open={showPdfViewer}
        onClose={() => {
          setShowPdfViewer(false);
          if (pdfUrl && pdfUrl.startsWith("blob:")) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl("");
          }
        }}
        title={pdfTitle}
        content={
          <iframe
            src={pdfUrl}
            width="100%"
            height="500px"
            style={{ border: "none" }}
            key={pdfUrl}
          />
        }
      />
    </>
  );
};

NotificarComunicacion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  comunicacion: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default NotificarComunicacion;
